/**
 * Storage keys the native session owns (M2, brief §2.5). The names are the
 * ones the kanfy-mobile builds already use; the native prefs store adds its
 * own `kanfy.prefs.` namespace. No imports, so the
 * auth storage adapter, the idle-logout hook and the local-first wipe can all
 * share them without an import cycle through the Supabase client.
 */

/** Absolute idle deadline (ms since epoch) in `storage.prefs`. */
export const IDLE_DEADLINE_PREF = 'session.deadline';

/**
 * The app lock's opt-in setting in `storage.prefs`: the owning sign-in's key
 * (see appLockOwnerOf), not a bare flag. A `'1'` left by an earlier build
 * matches no owner, so it is cleared rather than applied.
 */
export const APP_LOCK_ENABLED_PREF = 'appLock.enabled';

/** Failed unlocks since the last success, in `storage.prefs`, so a kill does not reset it. */
export const APP_LOCK_FAILURES_PREF = 'appLock.failures';

/**
 * The deadline an incomplete wipe leaves as its last durable guard: already
 * passed, so the next boot wipes again. The sign-out cleanup keeps it; a new
 * session written successfully makes it obsolete (Codex on #1093).
 */
export const WIPE_PENDING_DEADLINE = '0';

/**
 * Every pref that belongs to the signed-in session and must go with it on
 * sign-out or any wipe, so the next account on the phone never inherits them.
 */
export const NATIVE_SESSION_PREFS: readonly string[] = [
  IDLE_DEADLINE_PREF,
  APP_LOCK_ENABLED_PREF,
  APP_LOCK_FAILURES_PREF,
];

/**
 * Auth keys whose secure removal failed (a JSON array), in `storage.prefs`.
 * Deliberately NOT a session pref: it must outlive the session it ended. The
 * storage adapter hides these keys from auth-js and retries their removal
 * on every boot, so a Keystore failure cannot bring an ended session back.
 */
export const STALE_AUTH_KEYS_PREF = 'session.staleAuthKeys';

/**
 * Everything auth-js persists under the storage key: the session, the PKCE
 * verifier of a pending email link (installed `GoTrueClient.js:1466`), and the
 * separate user record when a user storage is configured (`:2356`).
 */
export function authKeysFor(authKey: string): string[] {
  return [authKey, `${authKey}-code-verifier`, `${authKey}-user`];
}
